import { useState, useEffect, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Wallet, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuth } from '@/contexts/AuthContext'
import { useOrg } from '@/contexts/OrgContext'
import { invokeWithFreshAuth } from '@/lib/edge-functions'
import { toast } from 'sonner'

// Wallet BC form — CREATE (no editTarget) and EDIT (editTarget set). Surfaces
// every input the wallet slides read. Maps 1:1 with WalletsInputs in
// supabase/functions/_shared/wallets-bc-math.ts. The 4 hard-required fields
// (current PSPs/q, avg ticket, tx/PSP/mo, take rate) are never invented — the
// edge fn returns 422 + reason if missing; here we require them client-side too.

export interface WalletEditTarget {
  slug: string
  clientName: string
  country: string | null
  language: 'es' | 'en' | 'pt'
  currency: string
  deckDate: string | null
  inputs: Record<string, unknown>
}

interface Props {
  open: boolean
  onClose: () => void
  onSaved?: (slug: string, url: string, net: number) => void
  editTarget?: WalletEditTarget | null
}

// Defaults align with the edge-fn benchmark defaults so blank → known fallback.
const DEFAULTS = {
  country: 'PE',
  language: 'es' as const,
  currency: 'USD',
  deckDate: '2026',
  currentPspsPerQ: '3',
  targetPspsPerQ: '17',
  avgTicket: '40',
  txPerPspMonth: '100000',
  walletTakeRate: '1.0',
  baseFeePerPspMonth: '3000',
  floorMonthly: '6000',
  capMonthly: '35000',
  pricingMode: 'saas_per_psp' as 'saas_per_psp' | 'per_tx' | 'hybrid',
  txPricingModel: 'tramos' as 'flat' | 'tramos' | 'tiers',
  ratePerTx: '0.03',
  txFloorMonthly: '6000',
  integrationWeeksCurrent: '8 – 12',
  integrationWeeksYuno: '2 – 4',
}

// Default rate ladder for tramos/tiers (last row upToTx='' = unlimited).
const DEFAULT_RATE_TIERS: Array<{ upToTx: string; ratePerTx: string }> = [
  { upToTx: '500000', ratePerTx: '0.04' },
  { upToTx: '2000000', ratePerTx: '0.03' },
  { upToTx: '', ratePerTx: '0.02' },
]

const PRICING_MODE_OPTIONS: Array<{ value: 'saas_per_psp' | 'per_tx' | 'hybrid'; label: string }> = [
  { value: 'saas_per_psp', label: 'SaaS por PSP / mes (floor + cap)' },
  { value: 'per_tx', label: 'Por transacción' },
  { value: 'hybrid', label: 'Híbrido (fijo por PSP + por transacción)' },
]
const TX_MODEL_OPTIONS: Array<{ value: 'flat' | 'tramos' | 'tiers'; label: string }> = [
  { value: 'flat', label: 'Flat (rate único)' },
  { value: 'tramos', label: 'Tramos (marginal por franja)' },
  { value: 'tiers', label: 'Tiers (todo el volumen al bracket)' },
]

const COUNTRY_OPTIONS = [
  { value: 'PE', label: 'Perú' },
  { value: 'MX', label: 'México' },
  { value: 'BR', label: 'Brasil' },
  { value: 'AR', label: 'Argentina' },
  { value: 'CO', label: 'Colombia' },
  { value: 'CL', label: 'Chile' },
  { value: 'US', label: 'United States' },
  { value: 'ES', label: 'España' },
]
const CURRENCY_OPTIONS = ['USD', 'MXN', 'BRL', 'ARS', 'COP', 'PEN', 'CLP', 'EUR', 'GBP']
const LANGUAGE_OPTIONS: Array<{ value: 'es' | 'en' | 'pt'; label: string }> = [
  { value: 'es', label: 'Español' },
  { value: 'en', label: 'English' },
  { value: 'pt', label: 'Português' },
]

function str(v: unknown): string {
  if (v === null || v === undefined) return ''
  return String(v)
}
// Blank → explicit null: the update endpoint deletes the key (so the AE can
// clear a stored value from the UI and the benchmark default re-applies with
// its warning), and the generate endpoint coerces null to "absent".
function maybeNum(payload: Record<string, unknown>, key: string, raw: string) {
  if (raw === '' || raw === null || raw === undefined) { payload[key] = null; return }
  const n = Number(raw)
  if (Number.isFinite(n)) payload[key] = n
}

export function NewWalletBcForm({ open, onClose, onSaved, editTarget }: Props) {
  const { user } = useAuth()
  const { org } = useOrg()
  const qc = useQueryClient()
  const isEdit = !!editTarget

  const [clientName, setClientName] = useState('')
  const [country, setCountry] = useState(DEFAULTS.country)
  const [language, setLanguage] = useState<'es' | 'en' | 'pt'>(DEFAULTS.language)
  const [currency, setCurrency] = useState(DEFAULTS.currency)
  const [deckDate, setDeckDate] = useState(DEFAULTS.deckDate)
  const [clientLogo, setClientLogo] = useState('')
  const [createdByEmail, setCreatedByEmail] = useState('')

  // Throughput (hard-required current; soft target)
  const [currentPspsPerQ, setCurrentPspsPerQ] = useState(DEFAULTS.currentPspsPerQ)
  const [targetPspsPerQ, setTargetPspsPerQ] = useState(DEFAULTS.targetPspsPerQ)
  // Volume (hard-required)
  const [avgTicket, setAvgTicket] = useState(DEFAULTS.avgTicket)
  const [txPerPspMonth, setTxPerPspMonth] = useState(DEFAULTS.txPerPspMonth)
  const [walletTakeRate, setWalletTakeRate] = useState(DEFAULTS.walletTakeRate)
  // Yuno pricing (soft)
  const [pricingMode, setPricingMode] = useState<'saas_per_psp' | 'per_tx' | 'hybrid'>(DEFAULTS.pricingMode)
  const [baseFeePerPspMonth, setBaseFeePerPspMonth] = useState(DEFAULTS.baseFeePerPspMonth)
  const [floorMonthly, setFloorMonthly] = useState(DEFAULTS.floorMonthly)
  const [capMonthly, setCapMonthly] = useState(DEFAULTS.capMonthly)
  // Per-transaction pricing (only when pricingMode==='per_tx')
  const [txPricingModel, setTxPricingModel] = useState<'flat' | 'tramos' | 'tiers'>(DEFAULTS.txPricingModel)
  const [ratePerTx, setRatePerTx] = useState(DEFAULTS.ratePerTx)
  const [txFloorMonthly, setTxFloorMonthly] = useState(DEFAULTS.txFloorMonthly)
  const [rateTiers, setRateTiers] = useState<Array<{ upToTx: string; ratePerTx: string }>>(DEFAULT_RATE_TIERS)
  // Narrative (optional)
  const [integrationWeeksCurrent, setIntegrationWeeksCurrent] = useState(DEFAULTS.integrationWeeksCurrent)
  const [integrationWeeksYuno, setIntegrationWeeksYuno] = useState(DEFAULTS.integrationWeeksYuno)

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Hydrate fields when the dialog opens (create defaults or edit prefill).
  useEffect(() => {
    if (!open) return
    if (editTarget) {
      const i = editTarget.inputs || {}
      setClientName(editTarget.clientName || '')
      setCountry(editTarget.country || DEFAULTS.country)
      setLanguage(editTarget.language || DEFAULTS.language)
      setCurrency(editTarget.currency || DEFAULTS.currency)
      setDeckDate(editTarget.deckDate || DEFAULTS.deckDate)
      setClientLogo(str(i.client_logo))
      setCurrentPspsPerQ(str(i.current_psps_per_q) || DEFAULTS.currentPspsPerQ)
      setTargetPspsPerQ(str(i.target_psps_per_q) || DEFAULTS.targetPspsPerQ)
      setAvgTicket(str(i.avg_ticket_usd) || DEFAULTS.avgTicket)
      setTxPerPspMonth(str(i.tx_per_psp_per_month) || DEFAULTS.txPerPspMonth)
      setWalletTakeRate(str(i.wallet_take_rate_pct) || DEFAULTS.walletTakeRate)
      setBaseFeePerPspMonth(str(i.base_fee_per_psp_month) || DEFAULTS.baseFeePerPspMonth)
      setFloorMonthly(str(i.floor_monthly) || DEFAULTS.floorMonthly)
      setCapMonthly(str(i.cap_monthly) || DEFAULTS.capMonthly)
      setPricingMode(i.pricing_mode === 'per_tx' || i.pricing_mode === 'hybrid' ? i.pricing_mode : 'saas_per_psp')
      setTxPricingModel(i.tx_pricing_model === 'flat' || i.tx_pricing_model === 'tiers' ? i.tx_pricing_model : 'tramos')
      setRatePerTx(str(i.rate_per_tx) || DEFAULTS.ratePerTx)
      setTxFloorMonthly(str(i.tx_floor_monthly) || DEFAULTS.txFloorMonthly)
      {
        const rt = Array.isArray(i.rate_tiers) ? (i.rate_tiers as Array<{ upToTx?: unknown; ratePerTx?: unknown }>) : null
        setRateTiers(rt && rt.length
          ? rt.map((tr) => ({ upToTx: tr.upToTx == null ? '' : String(tr.upToTx), ratePerTx: str(tr.ratePerTx) }))
          : DEFAULT_RATE_TIERS)
      }
      setIntegrationWeeksCurrent(str(i.integration_weeks_current) || DEFAULTS.integrationWeeksCurrent)
      setIntegrationWeeksYuno(str(i.integration_weeks_yuno) || DEFAULTS.integrationWeeksYuno)
    } else {
      setClientName('')
      setCountry(DEFAULTS.country)
      setLanguage(DEFAULTS.language)
      setCurrency(DEFAULTS.currency)
      setDeckDate(DEFAULTS.deckDate)
      setClientLogo('')
      setCurrentPspsPerQ(DEFAULTS.currentPspsPerQ)
      setTargetPspsPerQ(DEFAULTS.targetPspsPerQ)
      setAvgTicket(DEFAULTS.avgTicket)
      setTxPerPspMonth(DEFAULTS.txPerPspMonth)
      setWalletTakeRate(DEFAULTS.walletTakeRate)
      setBaseFeePerPspMonth(DEFAULTS.baseFeePerPspMonth)
      setFloorMonthly(DEFAULTS.floorMonthly)
      setCapMonthly(DEFAULTS.capMonthly)
      setPricingMode(DEFAULTS.pricingMode)
      setTxPricingModel(DEFAULTS.txPricingModel)
      setRatePerTx(DEFAULTS.ratePerTx)
      setTxFloorMonthly(DEFAULTS.txFloorMonthly)
      setRateTiers(DEFAULT_RATE_TIERS)
      setIntegrationWeeksCurrent(DEFAULTS.integrationWeeksCurrent)
      setIntegrationWeeksYuno(DEFAULTS.integrationWeeksYuno)
    }
    setError(null)
    if (user?.email && !editTarget) setCreatedByEmail(user.email)
  }, [open, editTarget, user?.email]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!clientName.trim()) { setError('Cliente es requerido'); return }
    if (!isEdit && !createdByEmail.trim()) { setError('Gmail conectado es requerido para crear'); return }
    // Hard-required (anti-fabrication) — allow 0 for current_psps + take_rate.
    if (currentPspsPerQ.trim() === '' || !Number.isFinite(Number(currentPspsPerQ))) { setError('PSPs/trimestre actual es requerido (0 es válido)'); return }
    if (avgTicket.trim() === '' || !(Number(avgTicket) > 0)) { setError('Ticket promedio es requerido (> 0)'); return }
    if (txPerPspMonth.trim() === '' || !(Number(txPerPspMonth) > 0)) { setError('Transacciones por PSP/mes es requerido (> 0)'); return }
    if (walletTakeRate.trim() === '' || !Number.isFinite(Number(walletTakeRate))) { setError('Take rate de la wallet es requerido (0 es válido)'); return }
    // Per-transaction pricing validation (mirrors the edge-fn validateInputs).
    // Applies to per_tx AND hybrid; the tx floor only exists in pure per_tx.
    if (pricingMode !== 'saas_per_psp') {
      if (pricingMode === 'per_tx' && txFloorMonthly.trim() !== '' && !(Number(txFloorMonthly) >= 0)) { setError('Piso mensual inválido (≥ 0)'); return }
      if (txPricingModel === 'flat') {
        if (!(Number(ratePerTx) > 0 && Number(ratePerTx) <= 10)) { setError('Rate por transacción requerido (0 < r ≤ 10)'); return }
      } else {
        let prev = -1
        for (let k = 0; k < rateTiers.length; k++) {
          const row = rateTiers[k]
          const r = Number(row.ratePerTx)
          if (!(r > 0 && r <= 10)) { setError(`Tramo ${k + 1}: rate inválido (0 < r ≤ 10)`); return }
          const isLast = k === rateTiers.length - 1
          if (row.upToTx.trim() === '') {
            if (!isLast) { setError('Solo el último tramo puede quedar sin límite (∞)'); return }
          } else {
            const u = Number(row.upToTx)
            if (!Number.isInteger(u) || u <= prev) { setError(`Tramo ${k + 1}: el límite debe ser un entero mayor al anterior`); return }
            prev = u
          }
        }
      }
    }

    const inputs: Record<string, unknown> = {
      current_psps_per_q: Number(currentPspsPerQ),
      avg_ticket_usd: Number(avgTicket),
      tx_per_psp_per_month: Number(txPerPspMonth),
      wallet_take_rate_pct: Number(walletTakeRate),
    }
    maybeNum(inputs, 'target_psps_per_q', targetPspsPerQ)
    inputs.pricing_mode = pricingMode
    if (pricingMode !== 'saas_per_psp') {
      inputs.tx_pricing_model = txPricingModel
      if (pricingMode === 'per_tx') maybeNum(inputs, 'tx_floor_monthly', txFloorMonthly)
      if (txPricingModel === 'flat') {
        inputs.rate_per_tx = Number(ratePerTx)
      } else {
        inputs.rate_tiers = rateTiers.map((row) => ({
          upToTx: row.upToTx.trim() === '' ? null : Number(row.upToTx),
          ratePerTx: Number(row.ratePerTx),
        }))
      }
    }
    if (pricingMode !== 'per_tx') {
      maybeNum(inputs, 'base_fee_per_psp_month', baseFeePerPspMonth)
      maybeNum(inputs, 'floor_monthly', floorMonthly)
      maybeNum(inputs, 'cap_monthly', capMonthly)
    }
    inputs.integration_weeks_current = integrationWeeksCurrent.trim() || null
    inputs.integration_weeks_yuno = integrationWeeksYuno.trim() || null
    if (currency.trim()) inputs.currency = currency.trim().toUpperCase()

    setSubmitting(true)
    try {
      const endpoint = isEdit ? 'wallets-bc-update' : 'wallets-bc-generate'
      const common = {
        client_name: clientName.trim(),
        country,
        language,
        currency,
        deck_date: deckDate.trim() || null,
        client_logo: clientLogo.trim() || null,
        inputs,
      }
      const body: Record<string, unknown> = isEdit
        ? { slug: editTarget!.slug, ...common }
        : { createdByEmail: createdByEmail.trim(), org_id: org?.id, ...common }

      const { data, error: invokeErr } = await invokeWithFreshAuth<{
        slug?: string; url?: string; net_total_usd?: number
        business_case?: { net_total_usd?: number; has_revenue?: boolean }
        warnings?: Record<string, boolean>
      }>(endpoint, { body, timeoutMs: 60_000 })

      if (invokeErr) {
        const ctx = (invokeErr as { context?: Response }).context
        if (ctx && typeof ctx.json === 'function') {
          try {
            const b = await ctx.clone().json() as { error?: string; reason?: string } | null
            if (b?.error) throw new Error(b.reason ? `${b.error} (${b.reason})` : b.error)
          } catch (parseErr) {
            if (parseErr instanceof Error && parseErr.message && parseErr.message !== 'Unexpected end of JSON input') throw parseErr
          }
        }
        throw invokeErr
      }

      const result = data as {
        slug?: string; url?: string; net_total_usd?: number
        business_case?: { net_total_usd?: number; has_revenue?: boolean }
        warnings?: Record<string, boolean>
      } | null
      if (!result?.slug && !isEdit) throw new Error('Invalid server response (no slug)')

      const slug = result?.slug || editTarget?.slug || ''
      const url = result?.url || `https://chief.yuno.tools/wallet/${slug}`
      const net = result?.net_total_usd ?? result?.business_case?.net_total_usd ?? 0
      const warnings = result?.warnings || {}
      const warnKeys = Object.keys(warnings).filter((k) => warnings[k])

      qc.invalidateQueries({ queryKey: ['wallets_bc'] })
      const netLabel = net.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
      toast.success(isEdit ? `Wallet BC actualizado · ${clientName.trim()}` : `Wallet BC creado · ${clientName.trim()}`, {
        description: `Neto Y1+Y2: ${netLabel} · ${url}`,
        action: { label: 'Abrir', onClick: () => window.open(url, '_blank', 'noopener') },
      })
      if (warnKeys.length > 0) {
        toast.warning('Usé benchmarks por defecto', {
          description: `Confirmá con el cliente: ${warnKeys.join(', ')}`,
        })
      }

      onSaved?.(slug, url, net)
      onClose()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error generando el wallet BC'
      setError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  const updateTier = (idx: number, key: 'upToTx' | 'ratePerTx', val: string) =>
    setRateTiers((prev) => prev.map((tr, i) => (i === idx ? { ...tr, [key]: val } : tr)))
  const addTier = () => setRateTiers((prev) => [...prev, { upToTx: '', ratePerTx: '' }])
  const removeTier = (idx: number) => setRateTiers((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev))

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-primary" />
            {isEdit ? 'Editar Wallet BC' : 'Nuevo Wallet BC'}
          </DialogTitle>
          <DialogDescription>
            Caso de negocio para wallets. La unidad de valor es el throughput de integración (PSPs/trimestre). Los 4 campos requeridos no se inventan.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Identity */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label htmlFor="wb-client">Wallet / Cliente *</Label>
              <Input id="wb-client" value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Yape" autoFocus />
            </div>
            {!isEdit && (
              <div className="col-span-2">
                <Label htmlFor="wb-email">Gmail conectado (AE) *</Label>
                <Input id="wb-email" type="email" value={createdByEmail} onChange={(e) => setCreatedByEmail(e.target.value)} placeholder="tu@y.uno" />
              </div>
            )}
            <div>
              <Label>País</Label>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COUNTRY_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Idioma</Label>
              <Select value={language} onValueChange={(v) => setLanguage(v as 'es' | 'en' | 'pt')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LANGUAGE_OPTIONS.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Moneda (display)</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="wb-date">Fecha del deck</Label>
              <Input id="wb-date" value={deckDate} onChange={(e) => setDeckDate(e.target.value)} placeholder="2026" />
            </div>
            <div className="col-span-2">
              <Label htmlFor="wb-logo">Logo del cliente (URL, opcional)</Label>
              <Input id="wb-logo" value={clientLogo} onChange={(e) => setClientLogo(e.target.value)} placeholder="https://…" />
            </div>
          </div>

          {/* Throughput */}
          <div>
            <p className="mb-2 text-sm font-semibold">Throughput de integración</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="wb-cur">PSPs/trimestre actual *</Label>
                <Input id="wb-cur" type="number" min="0" value={currentPspsPerQ} onChange={(e) => setCurrentPspsPerQ(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="wb-tgt">PSPs/trimestre con Yuno</Label>
                <Input id="wb-tgt" type="number" min="0" value={targetPspsPerQ} onChange={(e) => setTargetPspsPerQ(e.target.value)} placeholder="17 (benchmark)" />
              </div>
            </div>
          </div>

          {/* Volume */}
          <div>
            <p className="mb-2 text-sm font-semibold">Modelo de volumen</p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="wb-ticket">Ticket promedio (USD) *</Label>
                <Input id="wb-ticket" type="number" min="0" step="0.01" value={avgTicket} onChange={(e) => setAvgTicket(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="wb-tx">Tx por PSP/mes *</Label>
                <Input id="wb-tx" type="number" min="0" value={txPerPspMonth} onChange={(e) => setTxPerPspMonth(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="wb-take">Take rate wallet (%) *</Label>
                <Input id="wb-take" type="number" min="0" step="0.01" value={walletTakeRate} onChange={(e) => setWalletTakeRate(e.target.value)} />
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div>
            <p className="mb-2 text-sm font-semibold">Pricing Yuno (benchmark)</p>
            <div className="mb-3">
              <Label>Modelo de pricing</Label>
              <Select value={pricingMode} onValueChange={(v) => setPricingMode(v as 'saas_per_psp' | 'per_tx' | 'hybrid')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRICING_MODE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* SaaS component — saas_per_psp and hybrid */}
            {pricingMode !== 'per_tx' && (
              <div className={`grid grid-cols-3 gap-3${pricingMode === 'hybrid' ? ' mb-3' : ''}`}>
                <div>
                  <Label htmlFor="wb-base">Fee base / PSP / mes</Label>
                  <Input id="wb-base" type="number" min="0" value={baseFeePerPspMonth} onChange={(e) => setBaseFeePerPspMonth(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="wb-floor">Piso mensual</Label>
                  <Input id="wb-floor" type="number" min="0" value={floorMonthly} onChange={(e) => setFloorMonthly(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="wb-cap">Techo mensual</Label>
                  <Input id="wb-cap" type="number" min="0" value={capMonthly} onChange={(e) => setCapMonthly(e.target.value)} />
                </div>
              </div>
            )}

            {/* Per-transaction component — per_tx and hybrid. The tx floor only
                exists in pure per_tx: in hybrid the fixed part is the minimum. */}
            {pricingMode !== 'saas_per_psp' && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Modelo por transacción</Label>
                    <Select value={txPricingModel} onValueChange={(v) => setTxPricingModel(v as 'flat' | 'tramos' | 'tiers')}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TX_MODEL_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {pricingMode === 'per_tx' && (
                    <div>
                      <Label htmlFor="wb-txfloor">Piso mensual (mínimo)</Label>
                      <Input id="wb-txfloor" type="number" min="0" value={txFloorMonthly} onChange={(e) => setTxFloorMonthly(e.target.value)} />
                    </div>
                  )}
                </div>

                {txPricingModel === 'flat' ? (
                  <div>
                    <Label htmlFor="wb-rate">Rate por transacción (USD)</Label>
                    <Input id="wb-rate" type="number" min="0" step="0.0001" value={ratePerTx} onChange={(e) => setRatePerTx(e.target.value)} placeholder="0.03" />
                  </div>
                ) : (
                  <div>
                    <Label>Tramos (volumen mensual de tx → rate USD/tx · último tramo sin límite = ∞)</Label>
                    <div className="space-y-2">
                      {rateTiers.map((tr, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Input
                            type="number" min="0"
                            placeholder={idx === rateTiers.length - 1 ? '∞ (sin límite)' : 'hasta N tx/mes'}
                            value={tr.upToTx}
                            onChange={(e) => updateTier(idx, 'upToTx', e.target.value)}
                          />
                          <Input
                            type="number" min="0" step="0.0001"
                            placeholder="rate USD/tx"
                            value={tr.ratePerTx}
                            onChange={(e) => updateTier(idx, 'ratePerTx', e.target.value)}
                          />
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeTier(idx)} disabled={rateTiers.length <= 1}>×</Button>
                        </div>
                      ))}
                      <Button type="button" variant="outline" size="sm" onClick={addTier}>+ Agregar tramo</Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Narrative */}
          <div>
            <p className="mb-2 text-sm font-semibold">Narrativa (opcional)</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="wb-wc">Semanas integración (hoy)</Label>
                <Input id="wb-wc" value={integrationWeeksCurrent} onChange={(e) => setIntegrationWeeksCurrent(e.target.value)} placeholder="8 – 12" />
              </div>
              <div>
                <Label htmlFor="wb-wy">Semanas integración (Yuno)</Label>
                <Input id="wb-wy" value={integrationWeeksYuno} onChange={(e) => setIntegrationWeeksYuno(e.target.value)} placeholder="2 – 4" />
              </div>
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>Cancelar</Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEdit ? 'Actualizar' : 'Generar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
